import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useEditorStore } from "@/stores/editor/editor-store";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
} from "@/components/ui/dialog";
import { CreatePageModal } from "@/components/editor/CreatePageModal";
import { PageBrowserModal } from "@/components/editor/PageBrowserModal";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Plus,
  MoreHorizontal,
  Copy,
  Trash2,
  Settings as SettingsIcon,
  ChevronDown,
  ArrowUp,
  ArrowDown,
  Star,
  FileText,
  Home,
  Loader2,
  LayoutGrid,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Page, PageTemplate } from "@/types";
import { ProjectsApi } from "@/services/api/projects";
import { buildPersistedProjectState } from "@/services/persistence/persistedProjectState";
import { logger } from "@/utils";
import { toast } from "sonner";
import { useAuthStore } from "@/stores/auth/auth-store";
import { usePageBrowser } from "@/hooks/editor/usePageBrowser";
import {
  SHADOWS,
  DESERT_SUN,
} from "@/styles/tokens";

interface PageSelectorProps {
  className?: string;
}

interface StoppableEvent {
  stopPropagation(): void;
}

const deferUntilNextFrame = (callback: () => void) => {
  window.requestAnimationFrame(() => {
    callback();
  });
};

export const PageSelector: React.FC<PageSelectorProps> = ({ className }) => {
  const navigate = useNavigate();
  const { projectId } = useParams<{ projectId: string }>();

  const {
    currentPageId,
    currentPage,
    switchToPage,
    addPage,
    updatePage,
    deletePage,
    duplicatePage,
    reorderPages,
    canDeletePage,
    getPageComponentCount,
    project,
    getAllPagesWithComponents,
    syncCurrentPageComponents,
  } = useEditorStore();

  const pages = useEditorStore((state) => state.pages);
  const actualPages = React.useMemo(() => {
    if (!pages) return [];
    return Array.isArray(pages) ? pages : [];
  }, [pages]);

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [showNewPageDialog, setShowNewPageDialog] = useState(false);
  const [showPageSettingsDialog, setShowPageSettingsDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedPageForAction, setSelectedPageForAction] = useState<Page | null>(null);

  // Home page loading state
  const [homePageLoading, setHomePageLoading] = useState<string | null>(null);

  // Auth store selectors for home page
  const homeProjectId = useAuthStore((state) => state.user?.preferences?.homeProjectId);
  const homePageId = useAuthStore((state) => state.user?.preferences?.homePageId);
  const setHomePage = useAuthStore((state) => state.setHomePage);
  const clearHomePage = useAuthStore((state) => state.clearHomePage);

  // Page browser modal
  const pageBrowser = usePageBrowser();

  // Derived state
  const isCurrentProjectHome = project?.id ? homeProjectId === project.id : false;
  const isPageHome = (pageId: string) => isCurrentProjectHome && homePageId === pageId;

  // Page settings form state
  const [pageSettingsForm, setPageSettingsForm] = useState({
    name: "",
    description: "",
    navigation: {
      showInNavigation: true,
      navigationLabel: "",
      allowNavigation: true,
    },
  });

  // Helper function for template icons
  const getTemplateIcon = (template?: PageTemplate) => {
    switch (template) {
      case PageTemplate.DASHBOARD:
        return "📊";
      case PageTemplate.REPORT:
        return "📋";
      case PageTemplate.FORM:
        return "📝";
      case PageTemplate.ANALYTICS:
        return "📈";
      default:
        return "📄";
    }
  };

  // Handle page switching
  const handlePageSwitch = (pageId: string) => {
    if (pageId !== currentPageId && project?.id) {
      syncCurrentPageComponents();
      switchToPage(pageId);
      navigate(`/editor/${project.id}/page/${pageId}`);
      setIsDropdownOpen(false);
    }
  };

  // Handle new page
  const handleNewPage = () => {
    setIsDropdownOpen(false);
    deferUntilNextFrame(() => {
      setShowNewPageDialog(true);
    });
  };

  const handleCreatePage = async (formData: { name: string; description: string; template: PageTemplate }) => {
    const newPage = addPage(formData);

    setShowNewPageDialog(false);

    // Save the project after page creation
    try {
      if (projectId) {
        syncCurrentPageComponents();

        const saveData = buildPersistedProjectState({
          pagesWithComponents: getAllPagesWithComponents(),
          currentPageId,
          schemaVersion: project?.schemaVersion,
          lastModified: new Date().toISOString(),
          metadata: {
            action: "page_created",
            pageId: newPage.id,
            pageName: newPage.name,
          },
        });

        logger.info("Saving project after page creation:", {
          projectId,
          pageId: newPage.id,
          pageName: newPage.name,
          totalPages: saveData.pages.length,
        });

        const response = await ProjectsApi.saveProject(projectId, saveData);

        if (response.success) {
          logger.info("Project saved successfully after page creation");
        } else {
          logger.warn(
            "Failed to save project after page creation:",
            response.message,
          );
        }
      }
    } catch (error) {
      logger.error("Error saving project after page creation:", error);
    }

    // Navigate to new page
    setTimeout(() => {
      if (projectId) {
        navigate(`/editor/${projectId}/page/${newPage.id}`);
      }
    }, 100);
  };

  // Handle page settings
  const handlePageSettings = (page: Page, e: StoppableEvent) => {
    e.stopPropagation();
    setSelectedPageForAction(page);
    setPageSettingsForm({
      name: page.name,
      description: page.description || "",
      navigation: {
        showInNavigation: page.navigation?.showInNavigation ?? true,
        navigationLabel: page.navigation?.navigationLabel || "",
        allowNavigation: page.navigation?.allowNavigation ?? true,
      },
    });
    setIsDropdownOpen(false);
    deferUntilNextFrame(() => {
      setShowPageSettingsDialog(true);
    });
  };

  const handleSavePageSettings = () => {
    if (selectedPageForAction && pageSettingsForm.name.trim()) {
      updatePage(selectedPageForAction.id, {
        name: pageSettingsForm.name.trim(),
        description: pageSettingsForm.description,
        navigation: pageSettingsForm.navigation,
      });
      setShowPageSettingsDialog(false);
      setSelectedPageForAction(null);
    }
  };

  // Handle page deletion
  const handleDeletePage = (page: Page, e: StoppableEvent) => {
    e.stopPropagation();
    setSelectedPageForAction(page);
    setIsDropdownOpen(false);
    deferUntilNextFrame(() => {
      setShowDeleteDialog(true);
    });
  };

  const handleDelete = () => {
    if (selectedPageForAction) {
      deletePage(selectedPageForAction.id);
      setShowDeleteDialog(false);
      setSelectedPageForAction(null);
    }
  };

  // Handle page duplication
  const handleDuplicatePage = (page: Page, e: StoppableEvent) => {
    e.stopPropagation();
    const newName = `${page.name} Copy`;
    duplicatePage(page.id, newName);
    setIsDropdownOpen(false);
  };

  // Handle page reordering
  const movePageUp = (page: Page, e: StoppableEvent) => {
    e.stopPropagation();
    const currentIndex = actualPages.findIndex((p) => p.id === page.id);
    if (currentIndex > 0) {
      const newOrder = [...actualPages];
      [newOrder[currentIndex], newOrder[currentIndex - 1]] = [
        newOrder[currentIndex - 1],
        newOrder[currentIndex],
      ];
      reorderPages(newOrder.map((p) => p.id));
    }
  };

  const movePageDown = (page: Page, e: StoppableEvent) => {
    e.stopPropagation();
    const currentIndex = actualPages.findIndex((p) => p.id === page.id);
    if (currentIndex < actualPages.length - 1) {
      const newOrder = [...actualPages];
      [newOrder[currentIndex], newOrder[currentIndex + 1]] = [
        newOrder[currentIndex + 1],
        newOrder[currentIndex],
      ];
      reorderPages(newOrder.map((p) => p.id));
    }
  };

  // Handle setting page as home
  const handleSetAsHomePage = async (page: Page, e: StoppableEvent) => {
    e.stopPropagation();
    if (!project?.id) {
      toast.error("Unable to set home page: Project not loaded");
      return;
    }
    setHomePageLoading(page.id);
    try {
      await setHomePage(project.id, page.id);
      toast.success(`"${page.name}" set as home page`, {
        description: "This page will open automatically when you log in"
      });
      setIsDropdownOpen(false);
    } catch (error) {
      console.error("Failed to set home page:", error);
      toast.error("Failed to set home page");
    } finally {
      setHomePageLoading(null);
    }
  };

  // Handle removing page as home
  const handleRemoveAsHomePage = async (page: Page, e: StoppableEvent) => {
    e.stopPropagation();
    setHomePageLoading(page.id);
    try {
      await clearHomePage();
      toast.success(`"${page.name}" removed as home page`);
      setIsDropdownOpen(false);
    } catch (error) {
      console.error("Failed to remove home page:", error);
      toast.error("Failed to remove home page");
    } finally {
      setHomePageLoading(null);
    }
  };

  // Sort pages by order
  const sortedPages = React.useMemo(() => {
    return [...actualPages].sort((a, b) => (a.order || 0) - (b.order || 0));
  }, [actualPages]);

  // When starred pages exist, dropdown shows only starred; otherwise all pages
  const hasStarredPages = sortedPages.some((p) => p.isStarred);
  const dropdownPages = React.useMemo(() => {
    if (hasStarredPages) {
      return sortedPages.filter((p) => p.isStarred);
    }
    return sortedPages;
  }, [sortedPages, hasStarredPages]);

  const currentComponentCount = currentPageId ? getPageComponentCount(currentPageId) : 0;

  return (
    <div className="flex items-center gap-1">
      {/* Browse Pages button */}
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              className="h-8 w-8 p-0 shrink-0 bg-card border-input"
              onClick={() => pageBrowser.openModal()}
              aria-label="Browse all pages"
            >
              <LayoutGrid className="h-3.5 w-3.5 text-muted-foreground" />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="bottom">
            <p>Browse Pages</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>

    <TooltipProvider>
      <DropdownMenu open={isDropdownOpen} onOpenChange={setIsDropdownOpen}>
        <Tooltip>
          <TooltipTrigger asChild>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className={cn(
                  "h-8 min-w-[180px] max-w-[220px] justify-between px-2.5",
                  "transition-all duration-150 ease-out",
                  "bg-card border-input hover:bg-accent active:scale-[0.98]",
                  className
                )}
                aria-label={`Current page: ${currentPage?.name || "Select Page"}. Click to switch pages.`}
                aria-haspopup="menu"
              >
                <div className="flex items-center gap-1.5 min-w-0">
                  <FileText
                    className="w-4 h-4 flex-shrink-0 text-primary"
                  />
                  <span
                    className="text-sm font-medium truncate text-foreground"
                    style={{ maxWidth: "120px" }}
                  >
                    {currentPage?.name || "Select Page"}
                  </span>
                </div>
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  <Badge
                    variant="secondary"
                    className="text-[10px] px-1.5 py-px"
                  >
                    {currentComponentCount}
                  </Badge>
                  <ChevronDown
                    className="w-3.5 h-3.5 text-muted-foreground"
                  />
                </div>
              </Button>
            </DropdownMenuTrigger>
          </TooltipTrigger>
          <TooltipContent side="bottom">
            <p>Switch pages</p>
          </TooltipContent>
        </Tooltip>

        <DropdownMenuContent
          align="start"
          className="w-[280px] p-0 bg-popover border-border rounded-md"
          style={{
            boxShadow: SHADOWS.cardHover,
          }}
        >
          <ScrollArea className="max-h-[400px]">
            <div className="py-1">
              {dropdownPages.map((page, index) => {
                const isActive = page.id === currentPageId;
                const componentCount = getPageComponentCount(page.id);
                const canMoveUp = index > 0;
                const canMoveDown = index < dropdownPages.length - 1;

                return (
                  <div
                    key={page.id}
                    className={cn(
                      "group relative flex items-center justify-between px-2 py-1.5 cursor-pointer transition-all duration-150",
                      isActive
                        ? "bg-primary/10 border-l-[3px] border-l-primary"
                        : "border-l-[3px] border-l-transparent hover:bg-accent",
                    )}
                    onClick={() => handlePageSwitch(page.id)}
                  >
                    {/* Page info */}
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                      <span className="text-sm flex-shrink-0">
                        {getTemplateIcon(page.template)}
                      </span>
                      <span
                        className={cn(
                          "text-sm font-medium truncate",
                          isActive ? "text-primary" : "text-foreground",
                        )}
                      >
                        {page.name}
                      </span>
                      {page.isDefault && (
                        <Star
                          className="w-3 h-3 flex-shrink-0"
                          style={{ color: "#EAB308" }}
                          aria-label="Default page"
                        />
                      )}
                      {isPageHome(page.id) && (
                        <Home
                          className="w-3 h-3 flex-shrink-0"
                          style={{ color: DESERT_SUN.DEFAULT }}
                          aria-label="Home page"
                        />
                      )}
                    </div>

                    {/* Right side: badge and overflow menu */}
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      <Badge
                        variant="secondary"
                        className="text-[10px] px-1.5 py-px"
                      >
                        {componentCount}
                      </Badge>

                      {/* Overflow menu */}
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="opacity-0 group-hover:opacity-100 transition-opacity w-6 h-6 p-0"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <MoreHorizontal className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-[160px]">
                          <DropdownMenuItem
                            onClick={(e) => handlePageSettings(page, e)}
                          >
                            <SettingsIcon className="w-4 h-4 mr-2" />
                            Settings
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={(e) => handleDuplicatePage(page, e)}
                          >
                            <Copy className="w-4 h-4 mr-2" />
                            Duplicate
                          </DropdownMenuItem>
                          {/* Home Page Toggle */}
                          {isPageHome(page.id) ? (
                            <DropdownMenuItem
                              onClick={(e) => handleRemoveAsHomePage(page, e)}
                              disabled={homePageLoading === page.id}
                              className="text-[#E27D3D] focus:text-[#E27D3D]"
                            >
                              {homePageLoading === page.id ? (
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              ) : (
                                <Home className="w-4 h-4 mr-2" />
                              )}
                              Remove as Home Page
                            </DropdownMenuItem>
                          ) : (
                            <DropdownMenuItem
                              onClick={(e) => handleSetAsHomePage(page, e)}
                              disabled={homePageLoading === page.id}
                            >
                              {homePageLoading === page.id ? (
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              ) : (
                                <Home className="w-4 h-4 mr-2" />
                              )}
                              Set as Home Page
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            onClick={(e) => movePageUp(page, e)}
                            disabled={!canMoveUp}
                          >
                            <ArrowUp className="w-4 h-4 mr-2" />
                            Move Up
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={(e) => movePageDown(page, e)}
                            disabled={!canMoveDown}
                          >
                            <ArrowDown className="w-4 h-4 mr-2" />
                            Move Down
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            onClick={(e) => handleDeletePage(page, e)}
                            disabled={!canDeletePage(page.id)}
                            className="text-destructive focus:text-destructive"
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                );
              })}
            </div>
          </ScrollArea>

          {/* Browse All Pages (shown when dropdown is filtered to starred) */}
          {hasStarredPages && (
            <>
              <DropdownMenuSeparator className="my-0" />
              <DropdownMenuItem
                onClick={() => {
                  setIsDropdownOpen(false);
                  deferUntilNextFrame(() => {
                    pageBrowser.openModal();
                  });
                }}
                className="text-xs py-2 px-3 text-muted-foreground"
              >
                <LayoutGrid className="w-3.5 h-3.5 mr-2" />
                Browse All Pages...
              </DropdownMenuItem>
            </>
          )}

          <DropdownMenuSeparator className="my-0" />

          {/* New Page Button */}
          <div className="p-2">
            <Button
              variant="ghost"
              size="sm"
              className="w-full justify-start gap-2 text-primary font-medium"
              onClick={handleNewPage}
            >
              <Plus className="w-4 h-4" />
              New Page
            </Button>
          </div>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* New Page Dialog */}
      <CreatePageModal
        open={showNewPageDialog}
        onOpenChange={setShowNewPageDialog}
        onSubmit={handleCreatePage}
        defaultName={`Page ${actualPages.length + 1}`}
      />

      {/* Page Settings Dialog */}
      <Dialog
        open={showPageSettingsDialog}
        onOpenChange={setShowPageSettingsDialog}
      >
        <DialogContent
          variant="light"
          className="sm:max-w-[540px] p-0 gap-0 overflow-hidden"
        >
          <div className="px-6 pt-6 pb-4">
            <div className="flex items-start gap-4">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10">
                <SettingsIcon className="h-5 w-5 text-primary" />
              </div>
              <div className="space-y-1">
                <h3 className="text-lg font-semibold text-foreground">
                  Page Settings
                </h3>
                <p className="text-sm text-muted-foreground">
                  Configure settings for "{selectedPageForAction?.name}".
                </p>
              </div>
            </div>
          </div>

          <div className="border-t border-border" />

          <div className="max-h-[70vh] space-y-5 overflow-y-auto px-6 py-5">
            <div className="space-y-2">
              <Label
                htmlFor="settings-page-name"
                className="text-sm font-medium text-foreground"
              >
                Name
              </Label>
              <Input
                id="settings-page-name"
                value={pageSettingsForm.name}
                onChange={(e) =>
                  setPageSettingsForm({
                    ...pageSettingsForm,
                    name: e.target.value,
                  })
                }
                className="h-10 border-border bg-background text-foreground placeholder:text-muted-foreground focus:border-primary focus:ring-primary"
              />
            </div>

            <div className="space-y-2">
              <Label
                htmlFor="settings-page-description"
                className="text-sm font-medium text-foreground"
              >
                Description
                <span className="ml-1 font-normal text-muted-foreground">
                  (Optional)
                </span>
              </Label>
              <Textarea
                id="settings-page-description"
                value={pageSettingsForm.description}
                onChange={(e) =>
                  setPageSettingsForm({
                    ...pageSettingsForm,
                    description: e.target.value,
                  })
                }
                className="min-h-[110px] resize-none border-border bg-background text-foreground placeholder:text-muted-foreground focus:border-primary focus:ring-primary"
                rows={4}
                placeholder="Add context for what this page is used for"
              />
            </div>

            <div className="space-y-4 rounded-xl border border-border bg-muted/30 p-4">
              <div className="space-y-1">
                <h4 className="text-sm font-semibold text-foreground">
                  Navigation Settings
                </h4>
                <p className="text-xs text-muted-foreground">
                  Control how this page appears in project navigation.
                </p>
              </div>

              <div className="flex items-start gap-3 rounded-lg border border-border bg-background px-3 py-3">
                <Checkbox
                  id="show-in-nav"
                  checked={pageSettingsForm.navigation.showInNavigation}
                  onCheckedChange={(checked) =>
                    setPageSettingsForm({
                      ...pageSettingsForm,
                      navigation: {
                        ...pageSettingsForm.navigation,
                        showInNavigation: checked === true,
                      },
                    })
                  }
                  className="mt-0.5"
                />
                <div className="space-y-1">
                  <Label
                    htmlFor="show-in-nav"
                    className="text-sm font-medium text-foreground"
                  >
                    Show in navigation
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Include this page in menus and page lists.
                  </p>
                </div>
              </div>

              <div className="space-y-2">
                <Label
                  htmlFor="nav-label"
                  className="text-sm font-medium text-foreground"
                >
                  Nav Label
                </Label>
                <Input
                  id="nav-label"
                  value={pageSettingsForm.navigation.navigationLabel}
                  onChange={(e) =>
                    setPageSettingsForm({
                      ...pageSettingsForm,
                      navigation: {
                        ...pageSettingsForm.navigation,
                        navigationLabel: e.target.value,
                      },
                    })
                  }
                  className="h-10 border-border bg-background text-foreground placeholder:text-muted-foreground focus:border-primary focus:ring-primary"
                  placeholder="Leave empty to use page name"
                />
              </div>

              <div className="flex items-start gap-3 rounded-lg border border-border bg-background px-3 py-3">
                <Checkbox
                  id="allow-nav"
                  checked={pageSettingsForm.navigation.allowNavigation}
                  onCheckedChange={(checked) =>
                    setPageSettingsForm({
                      ...pageSettingsForm,
                      navigation: {
                        ...pageSettingsForm.navigation,
                        allowNavigation: checked === true,
                      },
                    })
                  }
                  className="mt-0.5"
                />
                <div className="space-y-1">
                  <Label
                    htmlFor="allow-nav"
                    className="text-sm font-medium text-foreground"
                  >
                    Allow navigation to this page
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Prevent linked navigation from landing on this page when
                    disabled.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="flex flex-col-reverse gap-2 border-t border-border bg-muted/30 px-6 py-4 sm:flex-row sm:justify-end">
            <Button
              variant="outline"
              onClick={() => setShowPageSettingsDialog(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSavePageSettings}
              disabled={!pageSettingsForm.name.trim()}
            >
              Save Changes
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Page Confirmation */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Page</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{selectedPageForAction?.name}"?
              This action cannot be undone.
              {selectedPageForAction &&
                getPageComponentCount(selectedPageForAction.id) > 0 && (
                  <span className="block mt-2 text-destructive font-medium">
                    This page contains{" "}
                    {getPageComponentCount(selectedPageForAction.id)}{" "}
                    component(s) that will be permanently deleted.
                  </span>
                )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setShowDeleteDialog(false)}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground"
            >
              Delete Page
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </TooltipProvider>

      {/* Page Browser Modal */}
      <PageBrowserModal {...pageBrowser} />
    </div>
  );
};
